// ProgramCatalogContent.jsx
import Badge from '@/app/common/components/Badge'
import { Button } from '@/app/common/components/button/Button'
import { DotsVertical } from '@/app/common/icons/general'
import { cn } from '@/lib/utils'
import { ProgramCategory } from '@/queries/program_catalog/schema'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shad/ui/accordion'
import { Popover, PopoverContent, PopoverTrigger } from '@/shad/ui/popover'
import { SystemCatalogItemStatuses } from '../../system_catalog/constants/system-catalog.constants'
import { commandScore } from '../../system_catalog/utils/system-catalog.utils'
import DisableCategoryPopup from './_components/DisableCategoryPopup'
import RemoveItemPopup from './_components/DisableItemPopup'
import EditCategoryPopup from './_components/EditCategoryPopup'
import EditItemPopup from './_components/EditItemPopup'
import EnableProgramItemPopup from './_components/EnableItemPopup'
import EnableProgramCategoryPopup from './_components/EnableCategoryPopup'

interface ProgramCatalogContentProps {
  programCategories: ProgramCategory[] // Replace with proper type from your API
  searchTerm: string
  onRemoveItemSuccess: () => void
  statusFilter: SystemCatalogItemStatuses
}

function ProgramCatalogContent({
  programCategories,
  onRemoveItemSuccess,
  searchTerm,
  statusFilter,
}: ProgramCatalogContentProps) {
  const filteredCategories = programCategories
    .filter((category) => {
      if (statusFilter === 'All') {
        return category
      }
      return category.status === statusFilter
    })
    .map((item) => {
      const items = item.program_items
        .map((item) => item.global_item_name)
        .join(', ')
      const categoryScore = commandScore(
        item.global_category_name + items,
        searchTerm
      )
      return {
        item,
        score: categoryScore,
      }
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      return b.score - a.score
    })
    .map(({ item }) => item)

  if (programCategories.length === 0) {
    return (
      <div>
        <div className="text-gray-800 text-center text-sm py-10">
          No items or categories added to this program yet. <br />
          Switch to the System Catalog tab to categories and items.
        </div>
      </div>
    )
  }

  if (filteredCategories.length === 0) {
    return (
      <div className="text-gray-800 text-center text-sm py-10">
        No items or categories match the filter criteria.
      </div>
    )
  }

  const totalItems = filteredCategories.reduce(
    (acc, category) => acc + category.program_items.length,
    0
  )

  return (
    <div className="ml-4">
      <div className="flex items-center justify-between">
        <div className="mb-2 mt-6">
          <h3 className="font-medium text-gray-800">Program Catalog</h3>
          <p className="text-gray-600 text-sm">
            Manage items and categories in your program.
          </p>
        </div>
        <div>
          <div className="text-sm text-gray-600">
            {totalItems} items in {filteredCategories.length} categories
          </div>
        </div>
      </div>

      <Accordion
        type="multiple"
        className="border border-gray-200 p-4 rounded-md h-[60vh] max-h-[60vh] overflow-y-auto"
      >
        {filteredCategories.map((category) => (
          <AccordionItem key={category.id} value={category.id.toString()}>
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap items-center">
                <AccordionTrigger
                  className={cn('', {
                    'text-gray-500': category.status === 'Inactive',
                  })}
                >
                  <span className="font-medium text-gray-800 space-x-2 flex items-center">
                    <span>{category.global_category_name}</span>
                    <span className="text-xs pl-6 pr-2 text-gray-600">
                      {category.program_items.length} Items
                    </span>
                    ・
                    <Badge semanticColor="Informative">
                      {category.service_fee}% Fee
                    </Badge>
                    {category.status === 'Inactive' && (
                      <Badge semanticColor="Negative">Inactive</Badge>
                    )}
                  </span>
                </AccordionTrigger>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button size={'square'} variant={'outline'}>
                    <DotsVertical className="" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="text-left">
                  {category.status === 'Active' ? (
                    <DisableCategoryPopup
                      category={category}
                      onRemoveSuccess={onRemoveItemSuccess}
                    />
                  ) : (
                    <EnableProgramCategoryPopup
                      onEnableCategorySuccess={onRemoveItemSuccess}
                      programCategory={category}
                    />
                  )}
                  <EditCategoryPopup
                    category={category}
                    onEditSuccess={onRemoveItemSuccess}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <AccordionContent>
              {category.program_items.length > 0 ? (
                <div className="px-0">
                  {category.program_items
                    .sort((a) => {
                      return a.status === 'Active' ? -1 : 1
                    })
                    .map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between py-2 gap-1 ml-6 mr-8 border-b last:border-b-0 border-gray-200"
                      >
                        <div className={cn('space-x-4 flex items-center')}>
                          <div className="text-gray-600">
                            {item.global_item_name}
                          </div>

                          <Badge semanticColor="Neutral">
                            {item.service_fee}% Fee
                          </Badge>
                          {item.status === 'Inactive' && (
                            <Badge semanticColor="Negative">
                              {item.status}
                            </Badge>
                          )}
                        </div>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button size={'square'} variant={'outline'}>
                              <DotsVertical className="" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="text-left">
                            {item.status === 'Active' ? (
                              <RemoveItemPopup
                                onRemoveSuccess={onRemoveItemSuccess}
                                item={item}
                              />
                            ) : (
                              <EnableProgramItemPopup
                                onEnableCategorySuccess={onRemoveItemSuccess}
                                programItem={item}
                              />
                            )}
                            <EditItemPopup
                              item={item}
                              onEditSuccess={onRemoveItemSuccess}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-600">No items available</p>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}

export default ProgramCatalogContent
